import { useCallback, useState } from 'react'
import { Control, FormState, useFieldArray } from 'react-hook-form'
import { View } from 'react-native'
import { StyleSheet } from 'react-native-unistyles'
import { uuidv7 } from 'uuidv7'

import FosterMedicationItem from '@/components/fosters/cards/medications/FosterMedicationItem'
import { FosterForm } from '@/components/fosters/fosterFormSchema'
import InfoText from '@/components/shared/InfoText'
import UniButton from '@/components/unistyles/UniButton'
import MedicalInfoType from '@/enums/medicalInfoType'
import Species from '@/enums/species'
import FosterService from '@/services/fosterService'
import OrganizationService from '@/services/organizationService'
import sharedStyles from '@/styles/styles'

interface Props {
  control: Control<FosterForm, any>
  disabled: boolean
  formState: FormState<FosterForm>
  fosterId: string
}

const FosterMedications = ({
  control,
  disabled,
  formState,
  fosterId,
}: Props) => {
  const [expandedIndex, setExpandedIndex] = useState<number>(-1)

  const { foster } = FosterService.useFoster(fosterId)

  const preventionMedicalInfos = OrganizationService.useMedicalInfo(
    MedicalInfoType.Prevention,
    foster?.birthDate,
    (foster?.species as Species) || undefined
  )

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: 'medications',
    shouldUnregister: false,
    keyName: 'key',
  })

  const handleAppend = useCallback(() => {
    append({
      fosterId: fosterId,
      id: uuidv7(),
      givenBy: null,
      dueDate: null,
      name: '',
      notes: null,
      givenDate: null,
      frequency: null,
      isRepeating: false,
    })

    setExpandedIndex(fields.length)
  }, [append, fields.length, fosterId])

  return (
    <View style={styles.root}>
      <InfoText text={preventionMedicalInfos?.[0]?.text} />
      {fields.map((field, index) => (
        <FosterMedicationItem
          control={control}
          disabled={false}
          expanded={expandedIndex === index}
          formState={formState}
          index={index}
          key={`${field.key}-medications`}
          onRemove={remove}
          setExpanded={() =>
            setExpandedIndex(index === expandedIndex ? -1 : index)
          }
        />
      ))}
      <View style={sharedStyles.buttonRow}>
        <UniButton disabled={disabled} mode="outlined" onPress={handleAppend}>
          Add Medication
        </UniButton>
      </View>
    </View>
  )
}

const styles = StyleSheet.create((_theme) => {
  return {
    root: {
      flex: 1,
    },
  }
})

export default FosterMedications
